import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../config';
import { Theme } from '../ui/theme';
import { GameState } from '../systems/GameState';
import { OUTFITS } from '../data/outfits';
import { PETS } from '../data/pets';
import { OutfitIconRenderer } from '../systems/OutfitIconRenderer';
import { PetRenderer } from '../systems/PetRenderer';
import { svgStringToTexture } from '../utils/svgToTexture';
import { Button } from '../ui/Button';
import { AudioManager } from '../systems/AudioManager';
import type { HairStyle } from '../types';

const RARITY_COLOR: Record<string, number> = {
  common: 0xcccccc,
  rare:   0x6fd4c4,
  epic:   0x9b6bff,
  legend: 0xffc94c,
  season: 0xff6fa1,
};

const GRID_TOP = 200;
const GRID_BOTTOM = GAME_HEIGHT - 80;
const VISIBLE_HEIGHT = GRID_BOTTOM - GRID_TOP;

export class CollectionScene extends Phaser.Scene {
  private filter: 'outfits' | 'pets' = 'outfits';
  private gridContainer: Phaser.GameObjects.Container | null = null;

  // Scroll — target/current 분리
  private scrollY = 0;
  private targetScrollY = 0;
  private maxScrollY = 0;
  private isDragging = false;
  private dragStartY = 0;
  private dragStartScroll = 0;
  private dragLastY = 0;
  private dragLastTime = 0;
  private dragVelocity = 0;
  private scrollbarTrack: Phaser.GameObjects.Rectangle | null = null;
  private scrollbarThumb: Phaser.GameObjects.Rectangle | null = null;

  constructor() { super('CollectionScene'); }

  create(): void {
    AudioManager.setScene(this);
    AudioManager.playBgm('home');
    this.scene.launch('HUDScene');
    this.cameras.main.setBackgroundColor('#fef0f5');
    this.scrollY = 0;
    this.targetScrollY = 0;

    this.add.text(GAME_WIDTH / 2, 72, '📚 컬렉션 도감', {
      fontFamily: Theme.font.family,
      fontSize: '34px',
      fontStyle: '800',
      color: Theme.hex.text,
    }).setOrigin(0.5);

    const owned = GameState.data.ownedOutfits.length;
    const total = OUTFITS.length;
    const ownedPets = GameState.data.ownedPets.length;
    const totalPets = PETS.length;
    this.add.text(GAME_WIDTH / 2, 120, `의상 ${owned}/${total} · 펫 ${ownedPets}/${totalPets}`, {
      fontFamily: Theme.font.family,
      fontSize: '16px',
      fontStyle: '700',
      color: Theme.hex.textMid,
    }).setOrigin(0.5);

    // Tabs
    this._buildTabs();

    void this.renderGrid();

    new Button(this, 100, GAME_HEIGHT - 50, '← 홈', () => {
      this.cameras.main.fadeOut(200);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scene.stop('HUDScene');
        this.scene.start('HomeScene');
      });
    }, { width: 120, height: 48, style: 'secondary' });

    this._setupScrollInput();
  }

  private _buildTabs(): void {
    ['outfits', 'pets'].forEach((tab, i) => {
      const x = 540 + i * 240;
      const isActive = this.filter === tab;
      const g = this.add.graphics();
      g.fillStyle(isActive ? 0xff6fa1 : 0xfff5f8, 1);
      g.fillRoundedRect(x - 110, 136, 220, 48, 14);
      g.lineStyle(2, 0xff6fa1, 0.8);
      g.strokeRoundedRect(x - 110, 136, 220, 48, 14);

      const hit = this.add.rectangle(x, 160, 220, 48, 0, 0);
      hit.setInteractive({ useHandCursor: true });
      hit.on('pointerdown', () => {
        if (this.filter === tab) return; // no-op if same tab
        this.filter = tab as typeof this.filter;
        this.scrollY = 0;
    this.targetScrollY = 0;
        // Rebuild tabs and grid without full restart to preserve scroll state
        this._buildTabs();
        void this.renderGrid();
      });
      this.add.text(x, 160, tab === 'outfits' ? '👗 의상' : '🐾 펫', {
        fontFamily: Theme.font.family,
        fontSize: '18px',
        fontStyle: '800',
        color: isActive ? '#ffffff' : Theme.hex.text,
      }).setOrigin(0.5);
    });
  }

  private _setupScrollInput(): void {
    this.input.on('wheel', (_ptr: Phaser.Input.Pointer, _gos: unknown, _dx: number, dy: number) => {
      this.targetScrollY = Phaser.Math.Clamp(this.targetScrollY + dy * 0.6, 0, Math.max(0, this.maxScrollY));
    });

    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
      this.isDragging = true;
      this.dragStartY = p.y;
      this.dragStartScroll = this.scrollY;
      this.dragLastY = p.y;
      this.dragLastTime = performance.now();
      this.dragVelocity = 0;
      this.targetScrollY = this.scrollY;
    });

    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (!this.isDragging) return;
      const now = performance.now();
      const dt = Math.max(1, now - this.dragLastTime);
      this.dragVelocity = (this.dragLastY - p.y) / dt * 16;
      this.dragLastY = p.y;
      this.dragLastTime = now;
      const newY = Phaser.Math.Clamp(this.dragStartScroll + (this.dragStartY - p.y), 0, Math.max(0, this.maxScrollY));
      this.scrollY = newY;
      this.targetScrollY = newY;
      if (this.gridContainer) this.gridContainer.y = GRID_TOP - this.scrollY;
      this._updateScrollbar();
    });

    const endDrag = () => {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.targetScrollY = Phaser.Math.Clamp(this.scrollY + this.dragVelocity * 12, 0, Math.max(0, this.maxScrollY));
    };
    this.input.on('pointerup', endDrag);
    this.input.on('pointerupoutside', endDrag);
  }

  update(_time: number, delta: number): void {
    if (!this.isDragging && Math.abs(this.targetScrollY - this.scrollY) > 0.1) {
      const t = Math.min(1, (delta / 16) * 0.22);
      this.scrollY += (this.targetScrollY - this.scrollY) * t;
      if (this.gridContainer) this.gridContainer.y = GRID_TOP - this.scrollY;
      this._updateScrollbar();
    }
  }


  private _updateScrollbar(): void {
    if (!this.scrollbarThumb || !this.scrollbarTrack) return;
    if (this.maxScrollY <= 0) { this.scrollbarThumb.setVisible(false); return; }
    this.scrollbarThumb.setVisible(true);
    const trackH = this.scrollbarTrack.height;
    const thumbH = Math.max(40, (VISIBLE_HEIGHT / (VISIBLE_HEIGHT + this.maxScrollY)) * trackH);
    const thumbY = this.scrollbarTrack.y - trackH / 2 + (this.scrollY / this.maxScrollY) * (trackH - thumbH) + thumbH / 2;
    this.scrollbarThumb.setDisplaySize(8, thumbH);
    this.scrollbarThumb.setPosition(this.scrollbarTrack.x, thumbY);
  }

  private async renderGrid(): Promise<void> {
    if (this.gridContainer) this.gridContainer.destroy();
    if (this.scrollbarTrack) this.scrollbarTrack.destroy();
    if (this.scrollbarThumb) this.scrollbarThumb.destroy();

    this.gridContainer = this.add.container(0, GRID_TOP);

    // Mask
    const maskShape = this.make.graphics({});
    maskShape.fillStyle(0xffffff);
    maskShape.fillRect(0, GRID_TOP, GAME_WIDTH, VISIBLE_HEIGHT);
    this.gridContainer.setMask(maskShape.createGeometryMask());

    const ownedOutfitIds = new Set(GameState.data.ownedOutfits.map(o => o.outfitId));
    const ownedPetIds = new Set(GameState.data.ownedPets.map(p => p.petId));

    const promises: Promise<void>[] = [];

    if (this.filter === 'outfits') {
      const startX = 220, cols = 8, cellW = 125, cellH = 125;
      const rows = Math.ceil(OUTFITS.length / cols);
      this.maxScrollY = Math.max(0, rows * cellH + 20 - VISIBLE_HEIGHT);

      OUTFITS.forEach((o, i) => {
        const x = startX + (i % cols) * cellW;
        const y = 10 + Math.floor(i / cols) * cellH;
        const owned = ownedOutfitIds.has(o.id);
        const rarityCol = RARITY_COLOR[o.rarity] ?? 0xcccccc;

        const shadow = this.add.graphics();
        shadow.fillStyle(0x3a2540, 0.08);
        shadow.fillRoundedRect(x - 52, y - 49 + 3, 104, 100, 12);

        const bg = this.add.graphics();
        bg.fillStyle(owned ? 0xffffff : 0xdddddd, 1);
        bg.fillRoundedRect(x - 52, y - 49, 104, 100, 12);
        bg.lineStyle(2.5, rarityCol, owned ? 1 : 0.3);
        bg.strokeRoundedRect(x - 52, y - 49, 104, 100, 12);

        this.gridContainer!.add([shadow, bg]);

        if (owned) {
          const hairStyle = (o.slot === 'hair' && o.hairStyle) ? o.hairStyle as HairStyle : undefined;
          const iconKey = `coll_icon_${o.id}`;
          const svgStr = OutfitIconRenderer.render(o.slot, o.color, hairStyle, o.iconStyle);
          const p = svgStringToTexture(this, iconKey, svgStr).then(() => {
            if (!this.scene.isActive()) return;
            const icon = this.add.image(x, y - 8, iconKey);
            icon.setScale(2.0);
            this.gridContainer!.add(icon);
          });
          promises.push(p);
        } else {
          const q = this.add.text(x, y - 8, '❓', { fontSize: '28px' }).setOrigin(0.5);
          this.gridContainer!.add(q);
        }

        const lbl = this.add.text(x, y + 36, owned ? o.name : '???', {
          fontFamily: 'Pretendard Variable, Pretendard, sans-serif',
          fontSize: '10px',
          fontStyle: '700',
          color: owned ? '#3a2540' : '#aaaaaa',
          wordWrap: { width: 96 },
          align: 'center',
        }).setOrigin(0.5);
        this.gridContainer!.add(lbl);
      });
    } else {
      const startX = 280, cols = 5, cellW = 200, cellH = 200;
      const rows = Math.ceil(PETS.length / cols);
      this.maxScrollY = Math.max(0, rows * cellH + 20 - VISIBLE_HEIGHT);

      PETS.forEach((p, i) => {
        const x = startX + (i % cols) * cellW;
        const y = 20 + Math.floor(i / cols) * cellH;
        const owned = ownedPetIds.has(p.id);
        const rarityCol = RARITY_COLOR[p.rarity] ?? 0xcccccc;

        const shadow = this.add.graphics();
        shadow.fillStyle(0x3a2540, 0.1);
        shadow.fillRoundedRect(x - 82, y - 82 + 4, 164, 164, 18);

        const bg = this.add.graphics();
        bg.fillStyle(owned ? 0xffffff : 0xdddddd, 1);
        bg.fillRoundedRect(x - 82, y - 82, 164, 164, 18);
        bg.lineStyle(3, rarityCol, owned ? 1 : 0.3);
        bg.strokeRoundedRect(x - 82, y - 82, 164, 164, 18);

        this.gridContainer!.add([shadow, bg]);

        if (owned) {
          const spriteKey = PetRenderer.getSpriteKey(p.species);
          if (this.textures.exists(spriteKey)) {
            const icon = this.add.image(x, y - 16, spriteKey).setScale(0.5);
            this.gridContainer!.add(icon);
          } else {
            const petSvg = PetRenderer.renderSVG(p.species);
            const petKey = `coll_pet_${p.id}`;
            const pp = svgStringToTexture(this, petKey, petSvg).then(() => {
              if (!this.scene.isActive()) return;
              const icon = this.add.image(x, y - 16, petKey);
              icon.setScale(2.0);
              this.gridContainer!.add(icon);
            });
            promises.push(pp);
          }
        } else {
          const ico = this.add.text(x, y - 16, '❓', { fontSize: '48px' }).setOrigin(0.5);
          this.gridContainer!.add(ico);
        }

        const lbl = this.add.text(x, y + 58, owned ? p.name : '???', {
          fontFamily: 'Pretendard Variable, Pretendard, sans-serif',
          fontSize: '14px',
          fontStyle: '800',
          color: owned ? '#3a2540' : '#aaaaaa',
        }).setOrigin(0.5);
        this.gridContainer!.add(lbl);
      });
    }

    // Scrollbar
    const sbX = GAME_WIDTH - 16;
    this.scrollbarTrack = this.add.rectangle(sbX, GRID_TOP + VISIBLE_HEIGHT / 2, 8, VISIBLE_HEIGHT, 0xddd0e6, 0.5);
    this.scrollbarThumb = this.add.rectangle(sbX, GRID_TOP + 20, 8, 40, 0xaa88cc);
    this._updateScrollbar();

    this.gridContainer.y = GRID_TOP - this.scrollY;
    await Promise.allSettled(promises);
  }
}
